import { useEffect, useState } from "react";
import { AppShell, SideGroup, SideLink } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, EmptyState, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { AddRepoForm, AppendList, ServerMark, ServiceTable } from "../components/Servers";
import { agoText } from "../chat/api";
import { useRouteParam } from "../lib/route";
import { appendTasks, repoKey, serversApi } from "../lib/servers";
import type { AppendSpec, Server } from "../lib/servers";
import { call, errText, tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 서버 — 나루(러너)별 서비스 · 포트 · 바로가기 · 리포 추가 ══
   시안: Design 캔버스 「나루 서비스 화면 시안」(Main · Add). `docs/decisions/servers.md`.
   목록은 열 때 한 번 읽고, 추가 뒤 · 「다시 읽기」에 다시 읽는다 — 타이머로 박동하지 않는다
   (서버 보고 자체가 몇 분 간격이라 화면이 자주 물어도 새 값이 없다). */

/** `GET /repos` 의 쓰는 부분 — 추가 폼의 고르기 목록. */
type Repo = { owner: string; name: string };

export function ServersPage() {
  const picked = useRouteParam("s");
  const [servers, setServers] = useState<Server[] | null>(null);
  const [tasks, setTasks] = useState<Issue[]>([]);
  const [repos, setRepos] = useState<string[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);

  function load() {
    serversApi
      .list()
      .then((s) => {
        setServers(s);
        setLoadErr(null);
      })
      .catch((e) => setLoadErr(errText(e)));
    // 「리포 추가」 이슈는 보드 목록에서 고른다. 못 읽어도 표는 그린다 — 목록만 빈다.
    tasksApi
      .list()
      .then(setTasks)
      .catch(() => setTasks([]));
  }
  useEffect(() => {
    load();
    call<Repo[]>(`/repos`)
      .then((rs) => setRepos(rs.map((r) => `${r.owner}/${r.name}`)))
      .catch(() => setRepos([]));
  }, []);

  const cur = servers?.find((s) => s.runner === picked) ?? servers?.[0] ?? null;

  async function append(spec: AppendSpec) {
    setBusy(true);
    setAddErr(null);
    try {
      const name = spec.service || spec.repo.split("/").pop()!;
      await serversApi.append(spec, tx("servers.add.taskTitle", { host: `s${spec.host}`, name }), tx("servers.add.taskIntent", { repo: spec.repo }));
      setAdding(false);
      load();
    } catch (e) {
      setAddErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const side =
    servers && servers.length > 1 ? (
      <>
        <SideGroup>{tx("servers.rail")}</SideGroup>
        {servers.map((s) => (
          <SideLink key={s.runner} icon="server" tip={s.runner} href={`#/servers?s=${encodeURIComponent(s.runner)}`} active={s.runner === cur?.runner}>
            {s.runner} {s.ip ? <span className="caption">{s.ip}</span> : null}
            {isDown(s) ? (
              <span className="caption svc__railDown" title={tx("runners.state.down")}>
                ○ {tx("runners.state.down")}
              </span>
            ) : null}
          </SideLink>
        ))}
      </>
    ) : undefined;

  return (
    <AppShell route="/servers" sidebarTitle={tx("servers.rail")} sidebar={side}>
      <PageHead
        title={cur ? cur.runner : tx("nav.servers")}
        meta={cur ? <ServerMark s={cur} /> : undefined}
        lead={cur ? reportLine(cur) : tx("servers.lead")}
        actions={
          <>
            <Button onClick={load}>{tx("common.action.refresh")}</Button>
            {cur?.host && !adding ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                {tx("servers.add.open")}
              </Button>
            ) : null}
          </>
        }
      />
      {loadErr ? <Alert tone="error">{tx("servers.loadFailed", { err: loadErr })}</Alert> : null}
      {!servers && !loadErr ? <Loading text={tx("servers.loading")} /> : null}
      {servers && servers.length > 0 ? <p className="caption svc__sum">{summaryLine(servers)}</p> : null}
      {cur && isDown(cur) ? <Alert tone="warning">{tx("servers.downNote", { ago: agoText(cur.downSince ?? null) })}</Alert> : null}
      {servers && servers.length === 0 ? <EmptyState icon="inbox" title={tx("servers.none.title")} desc={tx("servers.none.desc")} /> : null}
      {cur ? (
        <>
          {adding && cur.host ? (
            <AddRepoForm
              host={cur.host}
              used={(cur.services ?? []).flatMap((s) => s.ports.map((p) => p.value))}
              suggestions={repos}
              taken={(cur.services ?? []).map((s) => repoKey(s.repo))}
              busy={busy}
              error={addErr}
              onSubmit={append}
              onCancel={() => {
                setAdding(false);
                setAddErr(null);
              }}
            />
          ) : null}
          <ServiceTable server={cur} />
          <p className="caption svc__hint">{tx("servers.consoleHint")}</p>
          {cur.host ? <AppendList tasks={appendTasks(tasks, cur.host).slice(0, 5)} /> : null}
        </>
      ) : null}
    </AppShell>
  );
}

/** 끊긴 서버 — 붙은 연결이 없고 서버가 끊긴 시각을 준 것. 「안 붙어 봄」은 끊김이 아니다. */
function isDown(s: Server): boolean {
  return s.connections === 0 && !!s.downSince;
}

/** 「3대 · 나루 붙음 2 · 끊김 1」 — 화면 맨 위 한 줄. */
function summaryLine(servers: Server[]): string {
  return tx("servers.summary", {
    n: servers.length,
    live: servers.filter((s) => s.connections > 0).length,
    down: servers.filter(isDown).length,
  });
}

/** 「s114 · 192.168.5.114 · 2분 전 보고 · 서비스 2」 — 보고가 없으면 그렇게 말한다(0개로 안 적는다). */
function reportLine(s: Server): string {
  if (!s.reportedAt) return tx("servers.noReport.line");
  return tx("servers.reportLine", {
    host: `s${s.host}`,
    ip: s.ip ?? "",
    ago: agoText(s.reportedAt),
    n: s.services?.length ?? 0,
  });
}
